import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Spinner} from 'react-bootstrap';
import {BookmarkRemove} from '@mui/icons-material';
import {CircularProgress} from '@mui/material';
import {toast} from 'react-toastify';
import {AddRestockedToCartButton} from './AddRestockedToCartButton';
import {removePendingOrder} from '../infrastructure/pendingOrderApi';
import {isSuccess, isPending, isFailure} from '../../common/domain/RemoteData.model';
import {PriceWithFallback} from '../../common/ui/PriceWithFallback';
import {useComercialContext} from '../../comercial/ComercialContext';

const RemovePendingButton = ({product, onRemoved}) => {
    const [loading, setLoading] = useState(false);
    const {actingAsCliente} = useComercialContext();

    const handleClick = async () => {
        setLoading(true);
        try {
            const result = await removePendingOrder(product.code, actingAsCliente?.numCliente);
            if (isSuccess(result)) {
                toast.success('Producto quitado de tus pedidos pendientes!');
                onRemoved(product.code);
            } else {
                toast.error('Error al quitar producto de tus pedidos pendientes');
            }
        } catch (error) {
            console.error('Failed to remove pending order:', error);
            toast.error('Error al quitar producto de tus pedidos pendientes');
        } finally {
            setLoading(false);
        }
    };

    return (
        <button type="button"
            className="btn btn-outline-warning"
            title="Quitar de pendientes"
            onClick={handleClick}
            disabled={loading}>
            {loading ? <CircularProgress size={20}/> : <BookmarkRemove/>}
        </button>
    );
};

/**
 * Panel desplegable de pedidos pendientes (equivalente al CartPreview del
 * carrito): lista los pendientes, y los que ya tienen stock se pueden añadir
 * al carrito directamente.
 *
 * @param {{pendingOrders: Object, setShowPanel: (show: boolean) => void}} props
 * pendingOrders es el resultado de usePendingOrders.
 */
export const PendingPreview = ({pendingOrders, setShowPanel}) => {
    const {pendingData, allPending, refresh, removeLocally} = pendingOrders;
    const navigate = useNavigate();

    const handleGoToProduct = (code) => {
        setShowPanel(false);
        navigate(`/articulos/${encodeURIComponent(code)}`);
    };

    return (
        <div className="d-flex flex-column gap-3 p-2">
            <h2 className="h5 mb-0">Pedidos pendientes</h2>
            {isPending(pendingData) && (
                <div className="text-center my-3">
                    <Spinner/>
                </div>
            )}
            {isFailure(pendingData) && (
                <div className="alert alert-danger py-2 mb-0">
                    No se pudieron cargar tus pedidos pendientes.
                </div>
            )}
            {isSuccess(pendingData) && allPending.length === 0 && (
                <span className="text-muted">No tienes pedidos pendientes.</span>
            )}
            {isSuccess(pendingData) && allPending.length > 0 && (
                <ul className="list-group">
                    {allPending.map((pendingOrder) => {
                        const {product, pendingQuantity, isRestocked} = pendingOrder;
                        return (
                            <li key={product.code} className="list-group-item">
                                <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                                    <img src={product.imageUrl}
                                        alt={product.name}
                                        style={{width: '48px', height: '48px', objectFit: 'contain', cursor: 'pointer'}}
                                        onClick={() => handleGoToProduct(product.code)}
                                    />
                                    <div className="flex-fill">
                                        <span role="button" onClick={() => handleGoToProduct(product.code)}>
                                            {product.name}
                                        </span>
                                        <br/>
                                        <small className="text-muted">
                                            REF: {product.code} · {pendingQuantity} ud. pendientes
                                        </small>
                                        <div>
                                            <PriceWithFallback data={{
                                                sellingPrice: product.price?.bestPrice,
                                                basePrice: product.price?.value
                                            }}/>
                                        </div>
                                    </div>
                                    <div className="d-flex flex-row gap-2 align-items-center">
                                        {isRestocked ? (
                                            <div style={{minWidth: '190px'}}>
                                                <AddRestockedToCartButton
                                                    pendingOrder={pendingOrder}
                                                    onAdded={removeLocally}
                                                    onFailed={refresh}
                                                />
                                            </div>
                                        ) : (
                                            <span className="badge bg-secondary">En espera</span>
                                        )}
                                        <RemovePendingButton product={product} onRemoved={removeLocally}/>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};
